import { FormLayout } from '../../../quest/program-wizard/common';
import { OktaUserInfo } from '@/app/core/auth';
import {
  useStepContext,
  useDataPart,
  useDataStore,
} from '@/app/core/components/step-wizard';
import { ModuleInputProps } from '@/app/features/products/common/components/input-props';
import {
  Flex,
  SimpleGrid,
  FormControl,
  GridItem,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Box,
  Grid,
  Tooltip,
  Input,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers';
import { AppUserProfile, USAAProductDetailsModel } from '@ssar-app/domain';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

export const ProductDetailsForm = (
  props: ModuleInputProps<{ productName: string }>,
) => {
  const { stepKey, validator } = props;
  const { data } = useDataStore();
  const { isStepActive, onStepDeactivate } = useStepContext();
  const userProfile: AppUserProfile = OktaUserInfo.getOktaUserInfo();
  const [dataPartState, setDataPartState] =
    useDataPart<USAAProductDetailsModel>(stepKey);

  const [model, setModel] = useState<Partial<USAAProductDetailsModel>>({
    ...(dataPartState.data || {}),
    userId:
      data?.loginType === 'okta-gdc' ? userProfile?.sub || 'admin' : 'admin',
  });
  const [isValidForm, setIsValidForm] = useState<boolean>(false);

  const formMethods = useForm<USAAProductDetailsModel>({
    defaultValues: model,
    mode: 'onChange',
    resolver: yupResolver(validator),
  });
  const { errors, control, formState, trigger } = formMethods;

  const updateState = useCallback(() => {
    setDataPartState((currentState) => {
      const newState = {
        ...currentState,
        isValid: isValidForm,
        isDirty: formState.isDirty,
        isValidAsDraft: model.programName
          ? model.programName?.length > 0
          : false,
        data: {
          ...currentState.data,
          ...model,
        },
      };
      return newState;
    });
  }, [formState, model, setDataPartState, isValidForm]);

  onStepDeactivate(() => {
    return new Promise((_resolve, _reject) => {
      trigger(['programName', 'userId']).finally(() => {
        updateState();
      });
      _resolve(true);
    });
  });

  useEffect(() => {
    if (model && validator) {
      validator
        .validate(model)
        .then(() => {
          setIsValidForm(true);
        })
        .catch((_error) => {
          setIsValidForm(false);
        });
    }
  }, [model, validator]);

  useEffect(() => {
    if (model) {
      updateState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    updateState();
  }, [isValidForm, updateState]);

  if (!isStepActive) {
    return null;
  }

  return (
    <Flex direction='column' w='full'>
      <form>
        <FormLayout header='Name & User Id'>
          <SimpleGrid columns={1}>
            <Box>
              <FormControl isRequired isInvalid={!!errors.programName} mb={6}>
                <Grid templateColumns='repeat(4,1fr)'>
                  <GridItem justifyContent='end' display='flex'>
                    <FormLabel>Program Name :</FormLabel>
                  </GridItem>
                  <GridItem colSpan={3}>
                    <Controller
                      name='programName'
                      defaultValue={model?.programName || null}
                      control={control}
                      render={({ value, onChange }) => (
                        <Tooltip placement='top' label='Name of the Program'>
                          <Input
                            onChange={(e) => {
                              onChange(e.target.value);
                              setModel({
                                ...model,
                                programName: e.target.value,
                              });
                            }}
                            value={value}
                            placeholder='Program Name'
                            defaultValue={model?.programName}
                            spellCheck={false}
                          />
                        </Tooltip>
                      )}
                    ></Controller>
                    <FormErrorMessage>
                      {errors.programName?.message}
                    </FormErrorMessage>
                    <FormHelperText fontSize='xs'>
                      Note: Program Name must be at least 3 characters for auto
                      saving the program as a Draft
                    </FormHelperText>
                  </GridItem>
                </Grid>
              </FormControl>
            </Box>
            <Box>
              <FormControl isRequired isInvalid={!!errors.userId} mb={6}>
                <Grid templateColumns='repeat(4,1fr)'>
                  <GridItem justifyContent='end' display='flex'>
                    <FormLabel>User Id :</FormLabel>
                  </GridItem>
                  <GridItem colSpan={3}>
                    <Controller
                      name='userId'
                      defaultValue={model?.userId || 'admin'}
                      control={control}
                      render={({ value, onChange }) => (
                        <Tooltip placement='top' label='User Id'>
                          <Input
                            onChange={(e) => {
                              onChange(e.target.value);
                              setModel({
                                ...model,
                                userId: e.target.value,
                              });
                            }}
                            disabled
                            value={value}
                            placeholder='User Id'
                            defaultValue={model?.userId}
                            spellCheck={false}
                          />
                        </Tooltip>
                      )}
                    ></Controller>
                    <FormErrorMessage>
                      {errors.userId?.message}
                    </FormErrorMessage>
                  </GridItem>
                </Grid>
              </FormControl>
            </Box>
          </SimpleGrid>
        </FormLayout>
      </form>
    </Flex>
  );
};
